export interface ChatInlineRun {
  readonly bold: boolean;
  readonly italic: boolean;
  readonly strike: boolean;
  readonly mono: boolean;
  readonly value: string;
}

export type WhatsAppTextBlock =
  | { readonly kind: 'text'; readonly runs: readonly ChatInlineRun[] }
  | { readonly kind: 'quote'; readonly runs: readonly ChatInlineRun[] }
  | { readonly kind: 'bullet'; readonly runs: readonly ChatInlineRun[] }
  | { readonly kind: 'numbered'; readonly index: string; readonly runs: readonly ChatInlineRun[] }
  | { readonly kind: 'code'; readonly value: string };

const PLAIN: Omit<ChatInlineRun, 'value'> = {
  bold: false,
  italic: false,
  strike: false,
  mono: false,
};

type Style = Omit<ChatInlineRun, 'value'>;

const MARKERS = [
  { token: '```', key: 'mono' },
  { token: '**', key: 'bold' },
  { token: '*', key: 'bold' },
  { token: '_', key: 'italic' },
  { token: '~', key: 'strike' },
  { token: '`', key: 'mono' },
] as const;

function findClose(text: string, from: number, token: string): number {
  const allowBreak = token === '```';
  let index = from;
  while (index < text.length) {
    const next = text.indexOf(token, index);
    if (next === -1) return -1;
    const inner = text.slice(from, next);
    if (inner.length === 0 || inner.startsWith(' ') || inner.endsWith(' ')) {
      index = next + token.length;
      continue;
    }
    if (!allowBreak && inner.includes('\n')) {
      index = next + token.length;
      continue;
    }
    return next;
  }
  return -1;
}

function parseInline(text: string, style: Style): ChatInlineRun[] {
  const runs: ChatInlineRun[] = [];
  let plain = '';
  let index = 0;
  const flush = (): void => {
    if (plain.length === 0) return;
    runs.push({ ...style, value: plain });
    plain = '';
  };
  while (index < text.length) {
    const marker = MARKERS.find((item) => text.startsWith(item.token, index));
    if (marker === undefined) {
      plain += text.charAt(index);
      index += 1;
      continue;
    }
    const close = findClose(text, index + marker.token.length, marker.token);
    if (close === -1) {
      plain += marker.token;
      index += marker.token.length;
      continue;
    }
    const inner = text.slice(index + marker.token.length, close);
    flush();
    runs.push(...parseInline(inner, { ...style, [marker.key]: true }));
    index = close + marker.token.length;
  }
  flush();
  if (runs.length === 0) {
    return [{ ...style, value: text }];
  }
  return runs;
}

/** Splits WhatsApp markers (`*bold*`, `_italic_`, `~strike~`, `` `mono` ``, `**bold**`) into runs. */
export function parseChatInlineBold(text: string): ChatInlineRun[] {
  return parseInline(text, PLAIN);
}

/** List preview: same text without formatting markers. */
export function stripChatInlineBold(text: string): string {
  return parseWhatsAppFormatting(text)
    .map((block) => {
      if (block.kind === 'code') return block.value;
      return block.runs.map((run) => run.value).join('');
    })
    .join('\n');
}

function lineBlock(line: string): WhatsAppTextBlock {
  const quote = /^>\s?(.*)$/.exec(line);
  if (quote?.[1] !== undefined) {
    return { kind: 'quote', runs: parseInline(quote[1], PLAIN) };
  }
  const bullet = /^[*-]\s+(.*)$/.exec(line);
  if (bullet?.[1] !== undefined) {
    return { kind: 'bullet', runs: parseInline(bullet[1], PLAIN) };
  }
  const numbered = /^(\d+)\.\s+(.*)$/.exec(line);
  if (numbered?.[1] !== undefined && numbered[2] !== undefined) {
    return { kind: 'numbered', index: numbered[1], runs: parseInline(numbered[2], PLAIN) };
  }
  return { kind: 'text', runs: parseInline(line, PLAIN) };
}

/** WhatsApp formatting: inline markers plus quote, lists and fenced monospace. */
export function parseWhatsAppFormatting(text: string): WhatsAppTextBlock[] {
  const blocks: WhatsAppTextBlock[] = [];
  const fence = /```([\s\S]+?)```/g;
  let cursor = 0;
  for (const match of text.matchAll(fence)) {
    const start = match.index;
    const captured = match[1] ?? '';
    if (start > cursor) {
      for (const line of text.slice(cursor, start).split('\n')) {
        blocks.push(lineBlock(line));
      }
    }
    const value = captured.replace(/^\n/, '').replace(/\n$/, '');
    if (value.length > 0 && !value.startsWith(' ') && !value.endsWith(' ')) {
      blocks.push({ kind: 'code', value });
    } else {
      blocks.push({ kind: 'text', runs: parseInline(match[0], PLAIN) });
    }
    cursor = start + match[0].length;
  }
  if (cursor < text.length || blocks.length === 0) {
    for (const line of text.slice(cursor).split('\n')) {
      blocks.push(lineBlock(line));
    }
  }
  return blocks;
}
